import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';
import '../../pdf/draw/transforms';

/**
 * Kolecko (Circle/Wheel) - Circular layout with rotated labels
 *
 * Layout: Portrait page with large circle.
 * 10 items arranged radially around a main circle.
 * A smaller reference circle in the bottom-right corner.
 *
 * Migrated from C# EduFrame.Img10.Generators.Kolecko
 */
export class Kolecko implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 4;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page = pdf.addPage();

    const margin = PdfUnit.FromMillimeters(10);
    const s1 = PdfUnit.FromMillimeters(150);
    const s2 = PdfUnit.FromMillimeters(130);
    const topSize = PdfUnit.FromMillimeters(40);
    const topSize2 = PdfUnit.FromMillimeters(10);
    const imgSize = PdfUnit.FromMillimeters(23);

    // Main circle
    const sz1 = new PdfRect(margin, margin, s1, s1);
    const sz1x = new PdfRect(margin + (s1 - s2) / 2, margin + (s1 - s2) / 2, s2, s2);
    // Reference circle (bottom-right)
    const sz2 = new PdfRect(
      PdfSize.A4.width - s2 - margin,
      PdfSize.A4.height - s2 - margin,
      s2,
      s2
    );

    const center1 = new PdfPoint(sz1.centerX, sz1.centerY);
    const center2 = new PdfPoint(sz2.centerX, sz2.centerY);

    page.drawEllipse(GUtil.basePen, null, sz1);
    page.drawEllipse(GUtil.cutPen, null, sz1x);
    page.cutCircle(center1);

    page.drawEllipse(GUtil.basePen, null, sz2);
    page.cutCircle(center2);

    const itemCount = opts.list.length;

    // Pre-compute font for left labels using adjustFontMulti
    const allLeftTexts = opts.list.map((item) => item.left.getText() ?? '');
    const leftFont = page.adjustFontMulti(
      allLeftTexts.map((t) => [t]),
      GUtil.baseFont,
      new PdfSize(imgSize, imgSize)
    );

    // Draw items around the circle
    for (let i = 0; i < itemCount; i++) {
      const angle = (360 / itemCount) * i;

      page.save();
      page.rotateAtTransform(angle, center1);

      // Text label at top (near edge)
      const textRect = new PdfRect(
        center1.x - topSize / 2,
        center1.y - s1 * 0.5,
        topSize,
        topSize2
      );
      page.drawLabel(opts.list[i].right, { rect: textRect });

      // Image below text
      const imgRect = new PdfRect(center1.x - imgSize / 2, center1.y - s1 * 0.4, imgSize, imgSize);
      page.drawLabel(opts.list[i].left, { rect: imgRect, font: leftFont });

      page.restore();
    }

    // Question mark area in reference circle
    const qRect = new PdfRect(center2.x - imgSize / 2, center2.y - s1 * 0.4, imgSize, imgSize);
    page.drawRectangle(GUtil.basePen, null, qRect);
    page.drawLine(GUtil.basePen, qRect.topLeft, qRect.bottomRight);
    page.drawLine(GUtil.basePen, qRect.topRight, qRect.bottomLeft);
  }
}
